import { createContext, useContext } from "react";

/**
 * Fields whose schema `disabled` flag should be ignored for this render, with
 * the copy to show under each.
 *
 * A field can be disabled in the Tasker schema because its value is meant to
 * come from somewhere else — `vendor_legal_name` is filled from the PAN
 * registry rather than typed, so the two can never disagree. When that source
 * is unreachable the field would otherwise be both empty and uneditable, and a
 * required field in that state is a dead end for the whole form.
 *
 * A side-effect component (see PanNameAutofill) unlocks its own field when its
 * source fails, says why in `helperText`, and locks it again once the source
 * answers. The renderer knows nothing about any particular source.
 */
export type UnlockOptions = {
  /** Shown under the unlocked field. Falls back to a neutral line. */
  helperText?: string;
};

export type UnlockedFields = {
  unlocked: ReadonlyMap<string, UnlockOptions>;
  /** Unlock (or re-lock) one field by name. Safe to call from an effect. */
  setFieldUnlocked: (name: string, unlocked: boolean, options?: UnlockOptions) => void;
};

const NOOP: UnlockedFields = {
  unlocked: new Map<string, UnlockOptions>(),
  setFieldUnlocked: () => {},
};

export const UnlockedFieldsContext = createContext<UnlockedFields>(NOOP);

export function useUnlockedFields(): UnlockedFields {
  return useContext(UnlockedFieldsContext);
}
